import { useMutation } from '@tanstack/vue-query'
import type { CreateContractInput } from '~/resources/graphql/graphql'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useSetCartData } from '~/composables/use-cart'
import { useShowAlert } from '~/composables/use-show-alert'

export function useMembershipCreateContractMutation() {
    const query = useRpQuery()
    const activeFacility = useActiveFacility()
    const setCartData = useSetCartData()
    const showAlert = useShowAlert()

    return useMutation({
        mutationFn: async (mutationData: CreateContractInput) => {
            const response = await query(
                graphql(/** @lang GraphQL */ `
                    mutation MembershipCreateContractMutation(
                        $input: CreateContractInput!
                        $facilityId: ID!
                    ) {
                        createContract(contractInput: $input) {
                            __typename
                            ... on CreateContractResult {
                                recordId
                                record {
                                    __typename
                                    ... on PlanContract {
                                        ...PurchaseMembershipContractFragment
                                    }
                                }
                                query {
                                    viewer {
                                        cart(facilityId: $facilityId) {
                                            ...ViewerCartFragment
                                        }
                                    }
                                }
                            }
                            ... on ValidationError {
                                fieldErrors {
                                    message
                                }
                            }
                        }
                    }
                `),
                {
                    input: mutationData,
                    facilityId: activeFacility.value.id
                }
            )

            if (response.createContract.__typename === 'CreateContractResult') {
                // Update cart
                if (response.createContract.query.viewer !== null) {
                    setCartData(response.createContract.query.viewer.cart)
                }

                if (
                    response.createContract.record.__typename === 'PlanContract'
                ) {
                    return response.createContract.record
                } else {
                    throw new Error()
                }
            }

            if (response.createContract.fieldErrors.length > 0) {
                await showAlert(
                    'Heads Up!',
                    response.createContract.fieldErrors[0].message
                )
            }

            throw new Error()
        }
    })
}
